<template>
  <div :data-testid="testId">
    <div
      v-if="shouldShowHeader"
      :class="[headerContainerClass, 'd-flex justify-content-between align-items-center']"
    >
      <div
        v-if="shouldCenterTitle"
        :style="headerPlaceholderStyle"
      >
        <!-- Placeholder for centering the title -->
        &nbsp;
      </div>
      <div
        :class="{ 'flex-grow-1': !shouldCenterTitle }"
        class="d-flex align-items-center gap-2"
      >
        <!-- default header slot -->
        <slot name="header">
          <span :style="sectionHeaderTextColor">
            <slot name="title">
              <component
                :is="titleTag"
                :class="titleClasses"
                >{{ title }}</component
              >
            </slot>
          </span>
        </slot>
        <!-- second leftmost header content -->
        <slot name="header-left-2" />
        <!-- third leftmost header content -->
        <slot name="header-left-3" />
      </div>
      <!-- parent slot for all right aligned header content -->
      <slot name="header-right-parent">
        <div
          ref="rightAlignedHeaderContentRef"
          class="d-flex align-items-center justify-content-end gap-3"
        >
          <!-- second rightmost header content -->
          <div
            v-if="'header-right-2' in $slots"
            class="d-flex align-items-center justify-content-end"
          >
            <slot name="header-right-2" />
          </div>
          <!-- rightmost header content -->
          <slot name="header-right">
            <div
              v-if="!isPdf"
              class="d-flex gap-2 align-items-center"
            >
              <ExportGraphButton
                v-if="isChart && !isReadOnlyPlatform && shouldShowExportGraphButton"
                :disabled="shouldShowOverlay"
                :is-exporting-graph="isExportingGraph"
                :is-excel-export-implemented="isChartExcelExportImplemented"
                :is-copy-to-clipboard-implemented="isChartCopyToClipboardImplemented"
                @download="(format?: 'png' | 'xlsx') => $emit('download', format)"
                @copy="$emit('copy')"
              />
              <SettingsButton
                v-if="'menu-options-1' in $slots || 'menu-options-2' in $slots"
                menu-class="scroll-lg"
              >
                <slot name="menu-options-1" />
                <b-dd-divider v-if="'menu-options-1' in $slots && 'menu-options-2' in $slots" />
                <slot name="menu-options-2" />
              </SettingsButton>
            </div>
          </slot>
        </div>
      </slot>
    </div>
    <hr
      v-if="shouldShowHeader && shouldShowHr"
      :class="hrClasses"
      :style="hrStyle"
    />
    <div class="d-flex">
      <slot name="left-of-content" />
      <b-overlay
        :data-testid="`${testId ?? 'component-wrapper'}-overlay`"
        :show="shouldShowOverlay"
        :opacity="opacity ?? (errorOverlayParamsToUse !== null ? 1 : 0.85)"
        rounded="sm"
        class="flex-grow-1"
        :class="[overlayClass, errorOverlayParamsToUse !== null ? 'border rounded' : '']"
        :style="{ height: contentHeightToUse }"
      >
        <template
          v-if="errorOverlayParamsToUse !== null"
          #overlay
        >
          <slot name="overlay">
            <p
              v-if="errorOverlayParamsToUse"
              :data-testid="`${testId ?? 'component-wrapper'}-error`"
              class="text-center mb-0"
            >
              {{ translate(errorOverlayParamsToUse) }}
            </p>
          </slot>
        </template>
        <div
          :class="[...defaultSlotClasses, 'flex-grow-1']"
          :style="defaultSlotWrapperStyle"
        >
          <slot>
            <!-- chart content -->
          </slot>
        </div>
      </b-overlay>
    </div>
    <slot name="footer" />
  </div>
</template>
<script lang="ts">
import { computed, CSSProperties, defineComponent, PropType, ref } from 'vue';
import SettingsButton from '@/components/standard-components/StandardSettingsButton.vue';
import TranslateParams from '@/types/TranslateParams';
import { useIsPdf, usePdfPreferences } from '@/composables/usePdf';
import { useIsSystemLoading } from '@/composables/useSystemLoading';
import useTranslation from '@/composables/useTranslation';
import getEnv from '@/utils/getEnv';
import { useFeatureFlag } from '@/composables/useFeatureFlag';
import ExportGraphButton from '@/components/standard-components/ExportGraphButton.vue';
import ChartDefaults from '@/constants/ChartDefaults';
import useRouteChecks from '@/composables/useRouteChecks';
import { useRoute } from 'vue-router';
import { useAiReportState } from '@/no-code-dashboard/composables/useAiReportState';
import { useElementBounding } from '@vueuse/core';

/**
 * A unified wrapper component consisting of:
 * - a header, which is left-aligned (default) or centered
 * - an optional horizontal rule
 * - a content area, which is the main area of the component
 * - an optional footer
 *
 * This component is designed to standardize the common ways we organize components, headers, and related settings.
 * It affords two different slots for settings, menu-options-1 and menu-options-2. This allows the caller to provide
 * two sets of settings from two different parent components, key for the AI Report use case, which requires
 * both general component settings applicable to all components, as well as component-specific settings applicable to
 * only the specific component.
 *
 * This component is intended to replace:
 * - GraphPlaceholder
 * - ChartOverlay
 * - ConstituentRiskGraphWrapperDeprecated (probably)
 */
export default defineComponent({
  name: 'ComponentWrapper',
  components: {
    ExportGraphButton,
    SettingsButton,
  },
  props: {
    testId: {
      type: String,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    shouldCenterTitle: {
      type: Boolean,
      required: false,
      default: false,
    },
    shouldShowHeader: {
      type: Boolean,
      required: false,
      default: true,
    },
    shouldShowHr: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Extra classes for the header row (e.g. bottom margin when `shouldShowHr` is false).
     */
    headerContainerClass: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Extra classes for the content overlay (`b-overlay`), e.g. width or overflow utilities.
     */
    overlayClass: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Please do not add bottom margin/padding to this and instead handle it in the calling component.
     */
    hrClass: {
      type: String,
      required: false,
      default: '',
    },
    shouldHeaderHaveSpaceBelow: {
      type: Boolean,
      required: false,
      default: true,
    },
    shouldShowLoading: {
      type: Boolean,
      required: false,
    },
    errorOverlayParams: {
      type: Object as PropType<TranslateParams | null>,
      required: false,
      default: null,
    },
    /**
     * We allow passing either `errorOverlayParams` or `errorOverlayFn`.
     *
     * For some chart implementations, we pass `errorOverlayFn` to `useChartLifecycleUtilities` so that
     * the PDF does not continue waiting for a chart that has errored.
     */
    errorOverlayFn: {
      type: Function as PropType<() => TranslateParams | null>,
      required: false,
    },
    /**
     * Controls the opacity of the overlay shown
     *
     * The defaults:
     * - in an error state it is 1 (completely opaque)
     * - in the loading state it is 0.85 (the BootstrapVue default)
     */
    opacity: {
      type: Number,
      required: false,
    },
    /**
     * If you are manually setting the height of the component, as we do in `PerformanceContributionBreakdownSummary.vue`,
     * you may do so by supplying it to this prop.
     *
     * This is passed to the `b-overlay` component and the default slot content, meaning that the height of the entire
     * component will larger than this prop if the header or footer slots are used.
     */
    contentHeight: {
      type: Number,
      required: false,
    },
    isChart: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * By default, if `isChart` is true, we will add a negative margin to the right of the chart to align it with the
     * other content on the page.
     *
     * If you do not want this negative margin, you may set this prop to false.
     */
    shouldNotUseNegativeRightMargin: {
      type: Boolean,
      required: false,
    },
    shouldShowExportGraphButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    isExportingGraph: {
      type: Boolean,
      required: false,
      default: false,
    },
    isChartExcelExportImplemented: {
      type: Boolean,
      required: false,
      default: false,
    },
    isChartCopyToClipboardImplemented: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * This prop is needed for common components shared by HSBC which require disclaimerText to be passed to SectionTitle.vue
     */
    disclaimerText: {
      type: [String, Array] as PropType<string | string[]>,
      required: false,
      default: () => [],
    },
  },
  emits: {
    download: (_format?: 'png' | 'xlsx') => true,
    copy: () => true,
  },
  setup(props) {
    const { translate } = useTranslation();

    const isSystemLoading = useIsSystemLoading();

    const loading = computed(() => !isSystemLoading.value && props.shouldShowLoading);

    const isActuallyPdf = useIsPdf();
    const { shouldShowPdfPreview } = useAiReportState();
    const isPdf = computed(() => isActuallyPdf.value || shouldShowPdfPreview.value);

    const errorOverlayParamsToUse = computed(() => {
      if (props.errorOverlayParams !== null) return props.errorOverlayParams;

      if (props.errorOverlayFn !== undefined) return props.errorOverlayFn();

      return null;
    });

    const shouldShowOverlay = computed(() => {
      /**
       * if there's an error, do show the overlay
       */
      if (errorOverlayParamsToUse.value !== null) return true;

      /**
       * Otherwise, if it's a PDF we do not show a loading overlay in order to save CPU cycles on the remote session
       */
      if (isPdf.value) return false;

      return loading.value;
    });

    const { isBarclaysEnvironment, isBofAEnvironment, isHSBCEnvironment, isSocGenEnvironment } = getEnv();

    const hrClasses = computed(() => {
      // because our many components have varying degrees of top padding, please always leave mb-0 on the <hr> element,
      // and modify the top padding on the calling component itself as appropriate
      let base = `mt-1 mb-0`;

      if (isBarclaysEnvironment || isHSBCEnvironment || isSocGenEnvironment) {
        base += ' hr-accent';
      }

      if (isBofAEnvironment) {
        base += ' hr-info';
      }

      if (props.hrClass) {
        return `${base} ${props.hrClass}`;
      }

      return base;
    });

    const { sectionDividerColor, sectionHeaderTextColor } = usePdfPreferences();
    const hrStyle = computed(() => {
      const base = {
        'border-width': '2px',
      };

      // Let theme hr-* classes control color; inline border-color would override them.
      if (props.hrClass || isBarclaysEnvironment || isHSBCEnvironment || isBofAEnvironment || isSocGenEnvironment) {
        return base;
      }

      return {
        ...base,
        'border-color': sectionDividerColor.value,
      };
    });

    const titleTag = computed(() => {
      if (isPdf.value) return 'h4';

      return 'label';
    });

    const titleClasses = computed(() => {
      if (isPdf.value) return 'mb-0';

      return 'font-size-bigger font-weight-500 mb-0';
    });

    const { isReadOnlyPlatform } = useFeatureFlag();

    const contentHeightToUse = computed(() => {
      if (props.contentHeight != undefined) {
        return `${props.contentHeight}px`;
      }

      return '100%';
    });

    const route = useRoute();
    const { isOnAIReportPage } = useRouteChecks(route);

    const defaultSlotClasses = computed(() => {
      if (errorOverlayParamsToUse.value !== null) return ['rounded-border-canvas'];

      if (props.shouldShowHeader === true && props.shouldHeaderHaveSpaceBelow === true) return ['mt-3'];

      if (isOnAIReportPage.value && props.shouldHeaderHaveSpaceBelow !== false) {
        return ['mt-3'];
      }

      return [];
    });

    /**
     * Because of how we use negative margins to align the edges of our charts with other items on the page,
     * when we show the error overlay, some chart items may be peeking out behind it.
     *
     * In order to prevent this, we do not apply the negative margin when an error is shown.
     */
    const defaultSlotWrapperStyle = computed((): CSSProperties => {
      const base = {
        height: `${contentHeightToUse.value}`,
      };

      if (
        !props.isChart ||
        errorOverlayParamsToUse.value !== null ||
        props.shouldNotUseNegativeRightMargin ||
        shouldShowPdfPreview.value
      )
        return base;

      return {
        ...base,
        'margin-right': `-${ChartDefaults.DEFAULT_SPACING_RIGHT}px`,
      };
    });

    const rightAlignedHeaderContentRef = ref<HTMLDivElement | null>(null);
    const { width: rightAlignedHeaderContentWidth } = useElementBounding(rightAlignedHeaderContentRef);

    const headerPlaceholderStyle = computed((): CSSProperties | undefined => {
      if (props.shouldCenterTitle && rightAlignedHeaderContentWidth.value > 0) {
        return { width: `${rightAlignedHeaderContentWidth.value}px` };
      }

      return undefined;
    });

    return {
      translate,
      loading,
      isPdf,
      shouldShowOverlay,
      hrClasses,
      hrStyle,
      sectionDividerColor,
      sectionHeaderTextColor,
      titleTag,
      titleClasses,
      isReadOnlyPlatform,
      contentHeightToUse,
      defaultSlotClasses,
      defaultSlotWrapperStyle,
      rightAlignedHeaderContentRef,
      headerPlaceholderStyle,
      errorOverlayParamsToUse,
    };
  },
});
</script>
